import { orderingDiagnostics as genericOrderingDiagnostics } from 'modules/agreement/discountTree';
import { contextFields } from './form';
import { contexts, type ClientAgreementDiscountRule, type ClientDiscountContext } from './types';
import type { PolicyOrderingDiagnostic } from 'modules/agreement/factorPolicy/ordering';

export type ClientOrderingDiagnostic = {
  earlierRuleId: number;
  contexts: readonly ClientDiscountContext[];
};

/**
 * Finds only the deliberate, easy-to-explain overlaps in one endpoint's local order.
 * This is authoring feedback, not a resolver or a save-time validation rule.
 */
export function orderingDiagnostics(
  rules: readonly ClientAgreementDiscountRule[],
): ReadonlyMap<number, readonly ClientOrderingDiagnostic[]> {
  const diagnostics = genericOrderingDiagnostics(
    rules,
    contexts.flatMap((context) => [contextFields[context].client, contextFields[context].provider]),
    (rule) => [rule.target_activity?.id ?? null, rule.target_category?.id ?? null],
    (rule, field) => rule[field] != null,
  );
  return new Map(
    [...diagnostics].map(([ruleId, overlaps]) => [
      ruleId,
      overlaps.map(({ earlierRuleId, contexts: fields }) => ({
        earlierRuleId,
        contexts: contexts.filter((context) => {
          const pair = contextFields[context];
          return fields.includes(pair.client) || fields.includes(pair.provider);
        }),
      })),
    ]),
  );
}

export function policyOrderingDiagnostics(
  rules: readonly ClientAgreementDiscountRule[],
): ReadonlyMap<number, readonly PolicyOrderingDiagnostic[]> {
  return genericOrderingDiagnostics(
    rules,
    ['Client', 'Provider'] as const,
    (rule) => [rule.target_activity?.id ?? null, rule.target_category?.id ?? null],
    (rule, side) => (side === 'Client' ? rule.client_factor_policy : rule.provider_factor_policy) !== null,
  );
}
